import * as THREE from "three";
import { EnemyAct, LevelSim, SimEventKind } from "@highmarch/domain";
import type { VFX } from "../../../../art/vfx.js";
import { audio } from "../audio/audio";
import { reducedVfx } from "../ui/settings";
import { GroundMesh } from "./groundMesh";
import { AbilityRings, RING_LOOK } from "./views/abilityRings";
import { BurrowFx } from "./views/burrowFx";
import { RuneShields } from "./views/runeShields";
import type { EnemyView } from "./views/enemyView";

/** The stunned towers' dizzy stars and the stomp's shockwave (world units, seconds). */
export const STOMP_FX = {
  stars: [1.0, 0.86, 0.3] as [number, number, number],
  starsEvery: 0.32, starsHeight: 1.25, trauma: 0.22,
  wave: [1.0, 0.7, 0.3] as [number, number, number],
} as const;

/**
 * The M21 enemies' battle presentation in one place (docs/ENEMIES_V2.md): the Summoner's rune circle and its imps
 * rising out of it, the Burrower's dive (earth bursts, the mound ploughing along the road, the trail, the body sunk),
 * the Spellward's rune shield flaring when magic glances off, the Stunner's warning disc and its shockwave, and the
 * stars over the towers it stunned (they also droop like a bombed tower: views/towers.ts). Clips by name when the
 * model has them (`summon`, `burrow`, `surface`, `stomp`; `attack` stands in), sounds from the existing cues. Built by
 * the level scene, `onTick` after each sim tick (after the enemy views), `update` every frame.
 */
export class EnemiesV2Fx {
  readonly group = new THREE.Group();
  readonly rings: AbilityRings;
  readonly burrow: BurrowFx;
  readonly shields: RuneShields;
  /** Set by the level: the sprite VFX once loaded, and the camera kick. */
  fx: () => VFX | null = () => null;
  addTrauma: ((t: number) => void) | null = null;
  private readonly stunned = new Map<number, number>();
  private viewOf: (id: number) => EnemyView | undefined = () => undefined;
  private feet: (id: number) => THREE.Vector3 | null = () => null;

  constructor(private readonly sim: LevelSim) {
    const fx = () => this.fx();
    this.rings = new AbilityRings(sim, reducedVfx);
    this.burrow = new BurrowFx(sim, fx, reducedVfx);
    this.shields = new RuneShields(sim, reducedVfx);
    this.group.add(this.rings.group, this.burrow.group, this.shields.group);
  }

  /** Hooks into the enemy views (EnemyViews.viewOf, feet, drawnPoints). */
  attach(viewOf: (id: number) => EnemyView | undefined, feet: (id: number) => THREE.Vector3 | null, body: (id: number) => THREE.Vector3 | null): void {
    this.viewOf = viewOf; this.feet = feet;
    this.burrow.viewOf = viewOf; this.burrow.feet = feet;
    this.shields.viewOf = viewOf; this.shields.body = body;
  }

  onTick(): void {
    const sim = this.sim, fx = reducedVfx() ? null : this.fx();
    for (const ev of sim.events.all()) {
      switch (ev.kind) {
        case SimEventKind.SummonStarted: this.clip(ev.a, "summon"); audio.play("hexCurse"); break;
        case SimEventKind.ImpsSummoned: {
          const f = this.feet(ev.a);
          if (fx && f && ev.b > 0) {
            const at = new THREE.Vector3(f.x, GroundMesh.drawnAt(f.x, f.z) + 0.1, f.z);
            fx.flash(at, 1.1, RING_LOOK.summon);
            fx.sparkles(at, 14, { color: RING_LOOK.summon, radius: RING_LOOK.summonRadius, rise: 0.9, life: 0.7 });
            fx.ring(at, { size: RING_LOOK.summonRadius * 2.4, life: 0.4, color: RING_LOOK.summon, flat: true, bright: 1.6 });
          }
          if (ev.b > 0) audio.play("fire_arcane");
          break;
        }
        case SimEventKind.EnemyDigging: this.burrow.burst(ev.a, false); this.clip(ev.a, "burrow", true); audio.play("roadPlace"); break;
        case SimEventKind.EnemySurfaced: this.burrow.burst(ev.a, true); this.clip(ev.a, "surface"); audio.play("roadPlace"); break;
        case SimEventKind.StompWindup: this.clip(ev.a, "stomp"); audio.play("baseCharge"); break;
        case SimEventKind.EnemyStomped: this.shockwave(ev.a, ev.c / 100, fx); break;
        case SimEventKind.TowerDisabled:
          if (ev.c !== 0) this.stunned.set(ev.a, 0);                     // a Stunner's (a Bomber's names no enemy)
          break;
        case SimEventKind.CastInterrupted: this.broken(ev.a, ev.b === EnemyAct.Summoning ? RING_LOOK.summon : RING_LOOK.stomp, fx); break;
        case SimEventKind.MagicResisted: this.shields.flare(ev.a); this.clip(ev.a, "ward", false, false); audio.play("fire_beacon"); break;
        default: break;
      }
    }
  }

  /** Per frame: `alpha` the tick fraction, `dt` the frame's sim seconds. */
  update(alpha: number, dt: number): void {
    this.rings.update(alpha, dt);
    this.burrow.update(dt);
    this.shields.update(dt);
    const fx = reducedVfx() ? null : this.fx();
    for (const [id, next] of this.stunned) {
      const t = this.sim.findTower(id);
      if (!t || t.disabledTicksLeft <= 0) { this.stunned.delete(id); continue; }
      if (next > 0) { this.stunned.set(id, next - dt); continue; }
      this.stunned.set(id, STOMP_FX.starsEvery);
      if (fx) fx.sparkles(new THREE.Vector3(t.cell.x, GroundMesh.drawnAt(t.cell.x, t.cell.y) + STOMP_FX.starsHeight, t.cell.y), 3, { color: STOMP_FX.stars, radius: 0.32, rise: 0.15, life: 0.5, size: 0.12 });
    }
  }

  /**
   * A stun or Polymorph broke a cast or wind-up (CastInterrupted): the raider flinches out of it (its `hit` cuts the
   * `summon` / `stomp` clip), the rune circle or warning disc fades (abilityRings follows the act) and its colour
   * scatters as sparks with a "BROKEN" number, so the player sees the counter land.
   */
  private broken(id: number, color: [number, number, number], fx: VFX | null): void {
    const v = this.viewOf(id);
    if (v && v.rig.has("hit")) v.rig.playOnce("hit", false, null, { solo: true });
    audio.play("hexCurse");
    const f = this.feet(id);
    if (!fx || !f) return;
    const at = new THREE.Vector3(f.x, GroundMesh.drawnAt(f.x, f.z) + 0.15, f.z);
    fx.sparkles(at, 16, { color, radius: 0.55, rise: 0.5, life: 0.5 });
    fx.ring(at, { size: 1.4, life: 0.3, color, flat: true, bright: 1.2 });
    fx.number(at.clone().setY(at.y + 1.3), "BROKEN", { color, size: 0.3 });
  }

  /** The stomp lands: a shockwave ring the size of its reach, a dust wall, a crack and a small camera kick. */
  private shockwave(id: number, radius: number, fx: VFX | null): void {
    audio.play("mortarImpact");
    const f = this.feet(id);
    if (!f) return;
    this.addTrauma?.(STOMP_FX.trauma);
    if (!fx) return;
    const at = new THREE.Vector3(f.x, GroundMesh.drawnAt(f.x, f.z) + 0.05, f.z);
    fx.ring(at, { size: radius * 2.1, life: 0.45, color: STOMP_FX.wave, flat: true, bright: 2.2 });
    fx.ring(at, { size: radius * 1.2, life: 0.3, color: STOMP_FX.wave, flat: true, bright: 1.4 });
    fx.dust(at, 22, { color: [0.6, 0.5, 0.38], size: 0.42, speed: radius * 1.6, life: 0.8 });
    fx.decal(at, { type: "crack", size: 1.3, life: 3.0, hold: 1.4, color: [0.12, 0.1, 0.08], bright: 0 });
    fx.flash(at, 0.9, STOMP_FX.wave);
  }

  /**
   * Plays `name` on enemy `id` when its model has it, else (`orAttack`) its `attack`; `hold` keeps the last frame (a
   * dug-in body). The Spellward's `ward` flinch plays only when nothing else is (magic pulses come five a second).
   */
  private clip(id: number, name: string, hold = false, orAttack = name !== "burrow" && name !== "surface"): void {
    const v = this.viewOf(id);
    if (!v) return;
    if (v.rig.has(name)) { if (name !== "ward" || !v.rig.busy) v.rig.playOnce(name, hold, null, { solo: name !== "ward" }); }
    else if (orAttack && !v.rig.busy) v.rig.playOnce("attack");
  }

  dispose(): void { this.rings.dispose(); this.burrow.dispose(); this.shields.dispose(); this.stunned.clear(); this.group.clear(); }
}
